import { ReceptionBellMoneyIcon } from "@xola/icons";
import { Loader } from "common";
import React from "react";
import { AbstractPartner, Audit, LineItem, LineItemType, PartnerObject, Purchase, PurchaseItem } from "types";
import { useResource } from "../../../../hooks/useResource";
import { Currency } from "../../../Common/Currency";
import { NoTranslate } from "../../../Common/NoTranslate";
import { TimelineItem } from "../TimelineItem";

interface AffiliateCommissionTimelineItemProps {
    purchase: Purchase;
    item: PurchaseItem;
    audit: Audit;
    lineItem: LineItem;
    showDate?: boolean;
}

export const PartnerCommissionTimelineItem = ({
    purchase,
    item,
    audit,
    lineItem,
    showDate = false,
}: AffiliateCommissionTimelineItemProps) => {
    const { data: partner, isLoading } = useResource<AbstractPartner>(["partners", item.partner?.id], null, {
        enabled: !!item.partner?.id,
    });

    if (!PartnerCommissionTimelineItem.supportsAudit(purchase, item, audit, lineItem)) {
        return null;
    }

    if (isLoading) {
        return <Loader />;
    }

    const isAgent = partner?.object === PartnerObject.AFFILIATE;

    return (
        <TimelineItem
            title="Partner Commission"
            audit={lineItem}
            icon={<ReceptionBellMoneyIcon />}
            color="primary"
            showDate={showDate}
            purchase={purchase}
        >
            <div className="flex space-x-1">
                <Currency shouldRemoveTrailingZeroes={false}>{Math.abs(lineItem.amount)}</Currency>
                <span>
                    {" "}
                    was credited to the partner balance of{" "}
                    <NoTranslate className="capitalize">{partner ? getPartnerName(partner) : null}</NoTranslate>
                    {isAgent && "code" in partner && (
                        <NoTranslate className="uppercase"> ({partner?.code})</NoTranslate>
                    )}
                </span>
            </div>
        </TimelineItem>
    );
};

PartnerCommissionTimelineItem.supportsAudit = (
    _purchase: Purchase,
    _item: PurchaseItem,
    _audit: Audit,
    lineItem: LineItem,
) => {
    return lineItem?.type && lineItem.type === LineItemType.AFFILIATE_COMMISSION;
};

const getPartnerName = (partner: AbstractPartner) => {
    if (partner.object === PartnerObject.XOLA_PARTNER) {
        return partner.partnerSeller.name;
    }

    return partner.name;
};
